import {
  ReactElement,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { Switch, Route, useLocation, useRoute } from "_/components/router";
import { useAtom } from "jotai";
import { t } from "@lingui/core/macro";

import { Camera } from "_/components/camera";
import { NavCube } from "_/components/nav-cube";
import { PrepareViewerControlPanel } from "_/components/viewer-controls/prepare";
import { AnalyzeViewportToolbar } from "_/components/viewer-controls/analyze/viewport-toolbar";
import { StaleDataAlert } from "_/components/viewer-controls/analyze/stale-data-alert";
import { AnalyzeViewerControlPanel } from "_/components/viewer-controls/analyze";
import {
  OptimizeMainView,
  OptimizeViewerControlPanel,
} from "_/components/viewer-controls/optimize";
import { PrintView } from "_/components/view-loaders/analyze";
import { SimView } from "_/components/view-loaders/sim";
import { Bed } from "_/components/bed";
import { useAddToast } from "_/components/toasts";
import { Tooltip } from "_/components/tooltip";
import { Drawer } from "_/components/drawer";
import { Icon } from "_/components/icon";

import { projectRoutes, useOptimizeRoute } from "_/routes";

import { gpuWarningLastAcknowledgedAtom } from "_/state";

import { GCodeView } from "_/components/view-loaders/gcode";

import {
  type View,
  useViewerState,
  viewAtom,
  type ViewMode,
  glInfoAtom,
  BED_DIMENSIONS,
} from "_/state/viewer";

import { Project } from "_/data/projects";

import { getTheme } from "_/style";

import * as S from "./styled";

const generateViewObject = (view: View<ViewMode>): ReactElement | null => {
  if (view.mode === "prepare") {
    return <GCodeView view={view} key={view.id} />;
  } else if (view.mode === "analyze") {
    return <PrintView view={view} key={view.id} />;
  } else if (view.mode === "optimize") {
    return <SimView view={view} key={view.id} />;
  }

  return null;
};

const GPUNotificationBody = () => (
  <S.GPUNotificationBody>
    {t`components.viewer.windows-gpu-configuration-message`}
    <S.DocsLink
      href="https://basis.aon3d.com/docs/basis/troubleshooting_support/gpu_configuration"
      target="_blank"
    >
      {t`components.viewer.windows-gpu-configuration-doc`}
    </S.DocsLink>
  </S.GPUNotificationBody>
);

const GPUDetector = ({
  onIGPUDetected,
}: {
  onIGPUDetected?: () => void;
}): ReactElement => {
  const { gl } = useThree();
  const addToast = useAddToast();
  const [gpuWarningLastAcknowledged, setGpuWarningLastAcknowledged] = useAtom(
    gpuWarningLastAcknowledgedAtom
  );

  const [_glInfo, setGLInfo] = useAtom(glInfoAtom);

  const gpuWarningAcknowledgedThisWeek =
    Date.now() - gpuWarningLastAcknowledged < 7 * 24 * 60 * 60 * 1000;

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current || !gl) {
      return;
    }

    hasRun.current = true;

    // We know we're using WebGL2, so this cast is OK.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const glContext = gl.getContext() as WebGL2RenderingContext;

    const info = glContext.getExtension("WEBGL_debug_renderer_info");

    // Get the maximum texture size supported by the GPU
    // This is used to determine the dimensions of the sensor data array texture
    // oxlint-disable-next-line typescript/no-unsafe-assignment
    const maxTextureSize: number = glContext.getParameter(
      glContext.MAX_TEXTURE_SIZE
    );

    setGLInfo({ maxTextureSize });

    if (!info) {
      return;
    }

    // These parameters are untyped, so need a cast.
    // oxlint-disable-next-line typescript/no-unsafe-assignment
    const renderer: string = glContext.getParameter(
      info.UNMASKED_RENDERER_WEBGL
    );

    // Check if the browser is running on a Windows machine
    const userAgentRegex = /windows/i;
    const isWindows = userAgentRegex.test(navigator.userAgent);

    // Check if the unmasked renderer string indicates that the renderer is Intel (U)HD
    const rendererRegex = /intel.*u?hd/i;
    const isIntelIntegrated = rendererRegex.test(renderer);

    if (isWindows && isIntelIntegrated) {
      if (!gpuWarningAcknowledgedThisWeek) {
        addToast({
          kind: "warning",
          title: t`components.viewer.windows-gpu-configuration-title`,
          content: <GPUNotificationBody />,
          actions: [
            {
              label: t`common.ok`,
              action: () => {
                setGpuWarningLastAcknowledged(Date.now());
                onIGPUDetected?.();
              },
            },
          ],
        });
      } else {
        onIGPUDetected?.();
      }
    }
  }, [
    gl,
    addToast,
    gpuWarningLastAcknowledged,
    setGpuWarningLastAcknowledged,
    gpuWarningAcknowledgedThisWeek,
    onIGPUDetected,
    setGLInfo,
  ]);

  return <></>;
};

type ViewerCanvasProps = {
  /**
   * Project to view.
   */
  project: Project;

  /**
   * Callback function to be triggered if Windows + Intel (U)HD configuration is detected
   */
  onIGPUDetected?: () => void;
};

/**
 * Canvas / 3d viewer area of the project viewer.
 */

const ViewerCanvas = ({
  project,
  onIGPUDetected,
}: ViewerCanvasProps): ReactElement => {
  const [optActive, _optParams] = useRoute(projectRoutes.wildcards.optimize);

  const [viewerState, setViewerState] = useViewerState(
    project.id,
    optActive ? "optimize" : "prepareAnalyze"
  );

  const [viewState] = useAtom(viewAtom);

  const viewerContents = useMemo(() => {
    if (viewState) {
      return generateViewObject(viewState);
    }
    return null;
  }, [viewState]);

  const baseHeight = 250;

  const width = BED_DIMENSIONS.width;
  const length = BED_DIMENSIONS.height;

  const keyLightIntensity = 412_500;
  const fillLightIntensity = 212_500;
  const backLightIntensity = 150_000;

  // If the viewing angle is looking down at the part from above.
  const isTopDown = viewerState.cameraPosition[2] >= 0;

  return (
    <Suspense fallback={<S.CanvasFallback />}>
      <Canvas frameloop="demand">
        <GPUDetector onIGPUDetected={onIGPUDetected} />
        <Camera viewerState={viewerState} setViewerState={setViewerState} />
        <NavCube />
        <ambientLight intensity={0.75} />
        <pointLight
          position={[-100, width / 4, baseHeight + 100]}
          intensity={keyLightIntensity}
        />
        <pointLight
          position={[width, length / 4, baseHeight + 50]}
          intensity={fillLightIntensity}
        />
        <pointLight
          position={[-50, -50, baseHeight]}
          intensity={backLightIntensity}
        />
        <pointLight
          position={[width + 50, length + 50, baseHeight]}
          intensity={backLightIntensity}
        />
        <pointLight
          position={[width / 4, length + 50, baseHeight]}
          intensity={backLightIntensity}
        />
        {viewerContents}
        {!optActive && <Bed showBed={isTopDown} showGrid={!isTopDown} />}
      </Canvas>
    </Suspense>
  );
};

type ViewerControlPanelProps = {
  /**
   * Project to view.
   */
  project: Project;
};

/**
 * Control panel area of the project viewer.
 */
const ViewerControlPanel = ({
  project,
}: ViewerControlPanelProps): ReactElement => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const theme = getTheme("dark");

  return (
    <>
      <S.ControlPanelButton
        iconOnly
        kind="secondary"
        onClick={() => setIsPanelOpen(true)}
      >
        <Icon variant="ChevronLeft" />
      </S.ControlPanelButton>

      <Drawer
        open={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        width={400}
        side="right"
        background={theme.sidebar.background}
      >
        <S.ControlPanelContainer>
          <Switch>
            <Route path={projectRoutes.wildcards.prepare}>
              <PrepareViewerControlPanel project={project} />
            </Route>
            <Route path={projectRoutes.wildcards.analyze}>
              <AnalyzeViewerControlPanel project={project} />
            </Route>
            <Route path={projectRoutes.wildcards.optimize}>
              <OptimizeViewerControlPanel project={project} />
            </Route>
          </Switch>
        </S.ControlPanelContainer>
      </Drawer>
    </>
  );
};

/**
 * Warning icon / tooltip to be displayed in the viewer if Windows + Intel (U)HD
 * configuration is detected. When hovered over, a notification message linking to
 * the relevant documentation will be displayed.
 */
const ViewerGPUWarning = () => (
  <S.GPUWarningContainer>
    <Tooltip
      title={t`components.viewer.windows-gpu-configuration-title`}
      placement="rightEnd"
      wait={0}
      delay={0}
      content={<GPUNotificationBody />}
      target={<S.WarningIcon variant="Warning" />}
    />
  </S.GPUWarningContainer>
);

export const Viewer = ({ project }: { project: Project }): ReactElement => {
  const [location, _setLocation] = useLocation();

  // The optimize tab has a non-Canvas based home page, and the Canvas viewer is
  // one level down at /optimize/:optimizationId/thermal.
  const optimizeRoute = useOptimizeRoute();
  const isOptimizeMain = optimizeRoute?.subview === "main";

  const [view, setView] = useAtom(viewAtom);

  const [showGPUWarning, setShowGPUWarning] = useState(false);

  useLayoutEffect(() => {
    setView(null);
  }, [location, setView]);

  return (
    <S.Wrapper>
      {isOptimizeMain ? (
        <OptimizeMainView
          project={project}
          optimizationId={optimizeRoute?.optimizationId}
        />
      ) : (
        <>
          {showGPUWarning && <ViewerGPUWarning />}
          <S.Viewport>
            <ViewerCanvas
              project={project}
              onIGPUDetected={() => {
                setShowGPUWarning(true);
              }}
            />
            {view?.mode === "analyze" && (
              <>
                <AnalyzeViewportToolbar controlsAtom={view.controlsAtom} />
                <StaleDataAlert controlsAtom={view.controlsAtom} />
              </>
            )}
          </S.Viewport>
          <ViewerControlPanel project={project} />
        </>
      )}
    </S.Wrapper>
  );
};
